/**
 * AI Insights Panel Component
 * VOY-12: Collapsible panel with AI suggestions, quick actions, and reminders
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronUp, Check, Plus } from 'lucide-react';
import { AIInsight } from '@/data/companion-timeline';

interface InsightsPanelProps {
  suggestions: AIInsight[];
  quickActions: AIInsight[];
  reminders: AIInsight[];
  defaultOpen?: boolean;
}

export function InsightsPanel({ suggestions, quickActions, reminders, defaultOpen = true }: InsightsPanelProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="bg-white/[0.08] dark:bg-zinc-800/50 backdrop-blur-xl border border-white/30 dark:border-zinc-700/50 rounded-3xl overflow-hidden shadow-[0_8px_24px_rgba(0,0,0,0.15)] dark:shadow-xl">
      {/* Header - Collapsible */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 hover:bg-white/20 dark:hover:bg-zinc-700/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="text-2xl">💡</span>
          <h3 className="font-semibold text-gray-900 dark:text-white">AI Insights & Suggestions</h3>
        </div>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-[#5B6FED] dark:text-blue-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-[#5B6FED] dark:text-blue-400" />
        )}
      </button>

      {/* Content */}
      {isOpen && (
        <div className="p-4 pt-0 space-y-4">
          {/* Proactive Suggestions */}
          {suggestions.length > 0 && (
            <div>
              <h4 className="font-semibold text-sm text-gray-900 dark:text-white mb-2 flex items-center gap-1">
                🎯 Proactive Suggestions
              </h4>
              <ul className="space-y-2">
                {suggestions.map((suggestion, index) => (
                  <li key={index} className="text-sm text-gray-700 dark:text-zinc-300 flex items-start gap-2">
                    <span className="text-[#5B6FED] dark:text-blue-400 mt-0.5">•</span>
                    <span>{suggestion.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Quick Actions */}
          {quickActions.length > 0 && (
            <div>
              <h4 className="font-semibold text-sm text-gray-900 dark:text-white mb-2 flex items-center gap-1">
                ⚡ Quick Actions
              </h4>
              <div className="flex flex-wrap gap-2">
                {quickActions.map((action, index) => (
                  <Button
                    key={index}
                    onClick={action.action}
                    variant="outline"
                    size="sm"
                    className="rounded-full text-xs border-gray-300 text-gray-700 hover:bg-gray-100 dark:border-zinc-600 dark:text-zinc-300 dark:hover:bg-zinc-700/50"
                  >
                    {action.text}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Smart Reminders */}
          {reminders.length > 0 && (
            <div>
              <h4 className="font-semibold text-sm text-gray-900 dark:text-white mb-2 flex items-center gap-1">
                💡 Smart Reminders Set
              </h4>
              <ul className="space-y-2">
                {reminders.map((reminder, index) => (
                  <li key={index} className="text-sm text-gray-700 dark:text-zinc-300 flex items-center gap-2">
                    {reminder.completed ? (
                      <Check className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0" />
                    ) : (
                      <div className="w-4 h-4 border-2 border-[#5B6FED] dark:border-blue-400 rounded flex-shrink-0" />
                    )}
                    <span className={reminder.completed ? 'line-through opacity-70' : ''}>
                      {reminder.text}
                    </span>
                  </li>
                ))}
                <li className="text-sm text-[#5B6FED] dark:text-blue-400 flex items-center gap-2 cursor-pointer hover:text-[#4A5FDD] dark:hover:text-blue-300">
                  <Plus className="w-4 h-4 flex-shrink-0" />
                  <span className="font-medium">Add custom reminder</span>
                </li>
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

